<template>
  <div class="plans-audit">
    <div class="flex items-center justify-between mb-4">
      <h2>Auditoría de Planes</h2>
      <button
        class="secondary-button"
        :disabled="loading"
        @click="loadData"
      >
        Refrescar
      </button>
    </div>

    <p class="text-sm text-content-300 mb-4">
      Detecta planes con anomalías para revisión manual. Las 4 secciones muestran diferentes tipos de
      problemas: saldos fantasma, ciclos completados sin todas sus órdenes, creación manual de órdenes,
      y cambios manuales al saldo.
    </p>

    <!-- Filters -->
    <div class="card p-4 mb-4 flex flex-wrap gap-4 items-end">
      <div class="flex flex-col">
        <label class="text-xs font-medium">Umbral saldo mínimo ($)</label>
        <input
          type="number"
          v-model.number="filters.threshold"
          min="0"
          step="1"
          class="w-32"
          @change="loadData"
        />
      </div>
      <div class="flex flex-col">
        <label class="text-xs font-medium">Ventana (días)</label>
        <input
          type="number"
          v-model.number="filters.days"
          min="1"
          max="365"
          step="1"
          class="w-32"
          @change="loadData"
        />
      </div>
      <p class="text-xs text-content-300 ml-auto self-center" v-if="lastLoad">
        Última actualización: {{ lastLoad }}
      </p>
    </div>

    <!-- Tabs -->
    <div class="flex space-x-2 mb-4 border-b border-content-100">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        class="px-4 py-2 text-sm"
        :class="{
          'border-b-2 border-primary-500 font-semibold': activeTab === tab.id,
          'text-content-300': activeTab !== tab.id,
        }"
        @click="activeTab = tab.id"
      >
        {{ tab.label }}
        <span
          v-if="data && data[tab.id]"
          class="ml-1 inline-block px-2 py-0.5 text-xs rounded-full"
          :class="data[tab.id].length > 0 ? 'bg-warning-100 text-warning-700' : 'bg-content-100 text-content-500'"
        >
          {{ data[tab.id].length }}
        </span>
      </button>
    </div>

    <div v-if="loading" class="text-center p-8">
      <p class="text-content-300">Cargando auditoría...</p>
    </div>

    <div v-else-if="!data" class="text-center p-8">
      <p class="text-content-300">Click "Refrescar" para cargar los datos.</p>
    </div>

    <div v-else>
      <!-- Section 1: Phantom Budgets -->
      <div v-if="activeTab === 'phantom_budgets'">
        <p class="text-sm text-content-300 mb-3">
          Ciclos completados (todas las órdenes entregadas) con saldo remanente mayor a
          ${{ filters.threshold }}. Los casos <span class="font-medium text-error-600">phantom_credit</span>
          requieren corrección (el saldo está por encima de lo esperado); los
          <span class="font-medium text-warning-600">budget_below_expected</span> necesitan revisión manual.
        </p>
        <div v-if="data.phantom_budgets.length === 0" class="card p-4 text-center text-content-300">
          ✓ Sin saldos fantasma detectados.
        </div>
        <table v-else class="w-full text-sm">
          <thead>
            <tr class="text-left border-b border-content-100">
              <th class="p-2">Ciclo</th>
              <th class="p-2">Cliente</th>
              <th class="p-2">Saldo actual</th>
              <th class="p-2">Saldo esperado</th>
              <th class="p-2">Delta</th>
              <th class="p-2">Tipo</th>
              <th class="p-2">Acción</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in data.phantom_budgets"
              :key="row.order_cycle_id"
              class="border-b border-content-50 hover:bg-content-50"
            >
              <td class="p-2">#{{ row.order_cycle_id }}</td>
              <td class="p-2">{{ row.client_name }}</td>
              <td class="p-2">${{ formatCurrency(row.current_budget) }}</td>
              <td class="p-2">${{ formatCurrency(row.expected_budget) }}</td>
              <td class="p-2" :class="row.delta > 0 ? 'text-error-600' : 'text-warning-600'">
                {{ row.delta > 0 ? '+' : '' }}${{ formatCurrency(row.delta) }}
              </td>
              <td class="p-2">
                <span class="text-xs px-2 py-1 rounded-full" :class="caseTagClass(row.case_type)">
                  {{ caseTagLabel(row.case_type) }}
                </span>
              </td>
              <td class="p-2">
                <router-link
                  :to="{ name: 'quotesDetail', params: { id: row.quote_id } }"
                  class="link text-xs"
                >
                  Ver plan #{{ row.quote_id }}
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Section 2: Incomplete Cycles -->
      <div v-if="activeTab === 'incomplete_cycles'">
        <p class="text-sm text-content-300 mb-3">
          Ciclos marcados como completados (cycle_count ≥ total_cycles) pero con menos órdenes
          reales que sub-ciclos esperados. Estos indican un posible bug donde el cobro avanzó
          sin crear las órdenes correspondientes (ej. transacción parcial).
        </p>
        <div v-if="data.incomplete_cycles.length === 0" class="card p-4 text-center text-content-300">
          ✓ Todos los ciclos completados tienen sus órdenes.
        </div>
        <table v-else class="w-full text-sm">
          <thead>
            <tr class="text-left border-b border-content-100">
              <th class="p-2">Ciclo</th>
              <th class="p-2">Cliente</th>
              <th class="p-2">Sub-ciclos</th>
              <th class="p-2">Órdenes reales</th>
              <th class="p-2">Entregadas</th>
              <th class="p-2">Saldo</th>
              <th class="p-2">Acción</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in data.incomplete_cycles"
              :key="row.order_cycle_id"
              class="border-b border-content-50 hover:bg-content-50"
            >
              <td class="p-2">#{{ row.order_cycle_id }}</td>
              <td class="p-2">{{ row.client_name }}</td>
              <td class="p-2">{{ row.cycle_count }} / {{ row.total_cycles }}</td>
              <td class="p-2 text-warning-700">
                {{ row.sub_cycles_with_orders }} / {{ row.cycle_count }}
              </td>
              <td class="p-2">{{ row.delivered_orders }}</td>
              <td class="p-2">${{ formatCurrency(row.current_budget) }}</td>
              <td class="p-2">
                <router-link
                  :to="{ name: 'quotesDetail', params: { id: row.quote_id } }"
                  class="link text-xs"
                >
                  Ver plan #{{ row.quote_id }}
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Section 3: Manual Orders -->
      <div v-if="activeTab === 'manual_orders'">
        <p class="text-sm text-content-300 mb-3">
          Órdenes creadas fuera del horario del cron (06:00 AM) en los últimos {{ filters.days }} días.
          Probablemente creación manual vía admin. Límite de 200 resultados más recientes.
        </p>
        <div v-if="data.manual_orders.length === 0" class="card p-4 text-center text-content-300">
          Ninguna orden manual en el periodo.
        </div>
        <table v-else class="w-full text-sm">
          <thead>
            <tr class="text-left border-b border-content-100">
              <th class="p-2">Orden</th>
              <th class="p-2">Cliente</th>
              <th class="p-2">Mascota</th>
              <th class="p-2">Creada</th>
              <th class="p-2">Estado</th>
              <th class="p-2">Ciclo</th>
              <th class="p-2">Acción</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in data.manual_orders"
              :key="row.order_id"
              class="border-b border-content-50 hover:bg-content-50"
            >
              <td class="p-2">#{{ row.order_id }}</td>
              <td class="p-2">{{ row.client_name }}</td>
              <td class="p-2">{{ row.pet_name || '—' }}</td>
              <td class="p-2">{{ formatDateTime(row.created_at) }}</td>
              <td class="p-2">{{ row.status }}</td>
              <td class="p-2">#{{ row.order_cycle_id }}</td>
              <td class="p-2">
                <router-link
                  :to="{ name: 'clientsDetail', params: { id: row.client_id } }"
                  class="link text-xs"
                  v-if="row.client_id"
                >
                  Ver cliente
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Section 4: Budget Changes -->
      <div v-if="activeTab === 'budget_changes'">
        <p class="text-sm text-content-300 mb-3">
          Cambios manuales de saldo en los últimos {{ filters.days }} días (vía botón "Cambiar saldo
          actual" o por el script <code>fix:phantom-budget</code>). Cada fila muestra quién hizo el cambio
          y el contexto completo en el JSON.
        </p>
        <div v-if="data.budget_changes.length === 0" class="card p-4 text-center text-content-300">
          Sin cambios de saldo en el periodo.
        </div>
        <table v-else class="w-full text-sm">
          <thead>
            <tr class="text-left border-b border-content-100">
              <th class="p-2">Fecha</th>
              <th class="p-2">Admin</th>
              <th class="p-2">Cliente</th>
              <th class="p-2">Valor anterior</th>
              <th class="p-2">Valor nuevo</th>
              <th class="p-2">Motivo / Evento</th>
              <th class="p-2">Plan</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in data.budget_changes"
              :key="row.id"
              class="border-b border-content-50 hover:bg-content-50"
            >
              <td class="p-2 whitespace-nowrap">{{ formatDateTime(row.created_at) }}</td>
              <td class="p-2">{{ row.admin_name }}</td>
              <td class="p-2">{{ row.client_name }}</td>
              <td class="p-2">${{ formatCurrency(row.previous_value) }}</td>
              <td class="p-2">
                <span v-if="row.previous_state && row.previous_state.new_budget !== undefined">
                  ${{ formatCurrency(row.previous_state.new_budget) }}
                </span>
                <span v-else class="text-content-300">—</span>
              </td>
              <td class="p-2 text-xs">
                <div v-if="row.event" class="mb-1 text-content-500">{{ row.event }}</div>
                <div v-if="row.previous_state && row.previous_state.reason" class="italic">
                  "{{ row.previous_state.reason }}"
                </div>
                <div
                  v-if="row.previous_state && row.previous_state.warnings && row.previous_state.warnings.length > 0"
                  class="mt-1 text-warning-700"
                >
                  ⚠️ {{ row.previous_state.warnings.join(', ') }}
                </div>
              </td>
              <td class="p-2">
                <router-link
                  :to="{ name: 'quotesDetail', params: { id: row.quote_id } }"
                  class="link text-xs"
                  v-if="row.quote_id"
                >
                  Ver plan
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api";

export default {
  name: "PlansAudit",

  data() {
    return {
      loading: false,
      data: null,
      lastLoad: null,
      filters: {
        threshold: 10,
        days: 30,
      },
      activeTab: 'phantom_budgets',
      tabs: [
        { id: 'phantom_budgets', label: 'Saldos fantasma' },
        { id: 'incomplete_cycles', label: 'Ciclos sin órdenes' },
        { id: 'manual_orders', label: 'Órdenes manuales' },
        { id: 'budget_changes', label: 'Cambios de saldo' },
      ],
    };
  },

  created() {
    this.$store.commit("setSelectedTab", "audit");
    this.loadData();
  },

  methods: {
    async loadData() {
      this.loading = true;
      try {
        this.data = await api.audit.getPlansAudit({
          threshold: this.filters.threshold,
          days: this.filters.days,
        });
        this.lastLoad = new Date().toLocaleString('es-MX');
      } catch (error) {
        this.$store.dispatch("errorSnack",
          error?.response?.data?.message || "Error cargando auditoría"
        );
      }
      this.loading = false;
    },

    formatCurrency(value) {
      if (value === null || value === undefined) return '0.00';
      return Number(value).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    formatDateTime(iso) {
      if (!iso) return '—';
      try {
        return new Date(iso).toLocaleString('es-MX', {
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit',
        });
      } catch (e) {
        return iso;
      }
    },

    caseTagClass(type) {
      switch (type) {
        case 'phantom_credit': return 'bg-error-100 text-error-700';
        case 'budget_below_expected': return 'bg-warning-100 text-warning-700';
        default: return 'bg-content-100 text-content-500';
      }
    },

    caseTagLabel(type) {
      switch (type) {
        case 'phantom_credit': return 'Crédito fantasma';
        case 'budget_below_expected': return 'Revisión manual';
        default: return 'Residuo';
      }
    },
  },
};
</script>

<style scoped>
table {
  border-collapse: collapse;
}
</style>
